import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Bed, Bath, Maximize, MapPin, Crown, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { ToastAction } from './ui/toast';
import CompareButton from './CompareButton';
import HonestyBadge from './HonestyBadge';
import NeighbourhoodScorecard from './NeighbourhoodScorecard';
import SourceAttribution from './SourceAttribution';
import { useSecureAgency } from '../hooks/useSecureAgency';
import { useAuth } from '../context/AuthContext';
import { hasSaved, toggleSaved, subscribe as subscribeSaved } from '../utils/savedStore';
import { getCountry, propertyCountryCode, formatPrice } from '../lib/countries';
import { useMarket } from '../hooks/useMarket';

/**
 * Resolve a listing's tier from any of:
 *   property.tier, property.upgrade, or legacy boolean flags
 *   featured / urgent / boost.
 */
const resolveTier = (p) => {
  const explicit = (p?.tier || p?.upgrade || '').toString().toLowerCase();
  if (['gold', 'silver', 'bronze'].includes(explicit)) return explicit;
  if (p?.featured) return 'gold';
  if (p?.urgent) return 'silver';
  if (p?.boost) return 'bronze';
  return null;
};

const TIER_STYLES = {
  gold: {
    ring: 'ring-2 ring-amber-300 shadow-lg shadow-amber-100/60',
    badge: 'bg-amber-500 text-white',
    label: 'Gold',
  },
  silver: {
    ring: 'ring-2 ring-slate-300',
    badge: 'bg-slate-400 text-white',
    label: 'Silver',
  },
  bronze: {
    ring: 'ring-1 ring-orange-300',
    badge: 'bg-orange-500 text-white',
    label: 'Bronze',
  },
};

const PropertyCard = ({ property }) => {
  const market = useMarket();
  const propId = property?._id || property?.id;
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(() => hasSaved(propId));
  const tier = resolveTier(property);
  const tierStyle = tier ? TIER_STYLES[tier] : null;
  const secureAgency = useSecureAgency(property?.agency_id);

  useEffect(() => {
    setIsSaved(hasSaved(propId));
    return subscribeSaved(() => setIsSaved(hasSaved(propId)));
  }, [propId]);

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!propId) return;
    if (!isAuthenticated) {
      toast({
        title: 'Sign in to save',
        description: 'Create a free account to keep a shortlist across devices.',
      });
      navigate('/login');
      return;
    }
    try {
      const nowSaved = await toggleSaved(propId);
      // Undo action — reverts the toggle in place. Wrapped in its own
      // try/catch because a network hiccup on the undo shouldn't crash
      // the toast component.
      const undo = async () => {
        try { await toggleSaved(propId); } catch { /* silent */ }
      };
      toast({
        title: nowSaved ? 'Saved' : 'Removed from saved',
        description: nowSaved
          ? "It'll show up on your homepage next time."
          : 'Removed from your shortlist',
        action: (
          <ToastAction
            altText="Undo"
            onClick={undo}
            data-testid="toast-undo-save"
          >
            Undo
          </ToastAction>
        ),
      });
    } catch {
      toast({
        title: 'Could not update your shortlist',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    }
  };

  const countryCode = propertyCountryCode(property);
  const country = getCountry(countryCode);
  const fmtPrice = (n) => formatPrice(n, countryCode);
  // Use the property's OWN country to build its deep-link prefix so an IE
  // listing embedded on /uk still routes to /property/... (not /uk/property/),
  // and a UK listing embedded on / still routes to /uk/property/...
  const _prefix = countryCode === 'GB' ? '/uk'
    : countryCode === 'US' ? '/us'
    : countryCode === 'IE' ? ''
    : (market?.url_prefix || '');
  const propertyHref = `${_prefix}/property/${property._id}`;

  return (
    <Card
      className={`overflow-hidden rounded-2xl border border-gray-100 bg-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#7042cc]/10 transition-all duration-300 ${tierStyle ? tierStyle.ring : ''}`}
      data-tier={tier || 'standard'}
      data-testid={tier ? `property-card-${tier}` : 'property-card-standard'}
    >
      <Link to={propertyHref}>
        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={property.images[0]}
            alt={property.title}
            className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-500"
          />

          {/* Save (heart) — brand purple when saved */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 bg-white/95 hover:bg-white rounded-full shadow-sm h-9 w-9"
            onClick={handleSave}
            data-testid={`property-card-save-btn-${propId || 'unknown'}`}
            aria-pressed={isSaved}
            aria-label={isSaved ? 'Remove from saved' : 'Save this home'}
          >
            <Heart
              className={`h-[18px] w-[18px] ${isSaved ? 'fill-[#7042cc] text-[#7042cc]' : 'text-gray-700'}`}
            />
          </Button>

          <div className="absolute top-3 right-14" onClick={(e) => e.preventDefault()}>
            <CompareButton propertyId={property._id || property.id} />
          </div>

          {/* Sold / Sale-Agreed overlay — kept as a diagonal ribbon so it
              trumps the type pill visually. Seeded (external) listings that
              have transacted keep the card in the grid so the lead history
              stays intact and buyers see "yes, agents close deals here". */}
          {property.status && ['sold', 'sale_agreed'].includes((property.status || '').toLowerCase()) && (
            <>
              <div
                className="absolute inset-0 bg-black/35 pointer-events-none"
                data-testid={`status-overlay-${property.status}`}
              />
              <div
                className={`absolute top-6 -right-10 w-40 text-center py-1.5 rotate-45 shadow-md text-white text-[11px] font-extrabold tracking-widest uppercase pointer-events-none ${
                  property.status === 'sold' ? 'bg-rose-600' : 'bg-amber-500'
                }`}
                data-testid={`status-ribbon-${property.status}`}
              >
                {property.status === 'sold' ? 'Sold' : 'Sale Agreed'}
              </div>
            </>
          )}

          {/* Listing-type pill — soft tinted, brand purple for sale */}
          {property.type === 'rent' && (
            <Badge className="absolute top-3 left-3 bg-emerald-500 hover:bg-emerald-500 text-white rounded-full px-3 py-1 shadow-sm">
              To rent
            </Badge>
          )}
          {property.type === 'sale' && (
            <Badge className="absolute top-3 left-3 bg-[#7042cc] hover:bg-[#7042cc] text-white rounded-full px-3 py-1 shadow-sm">
              For sale
            </Badge>
          )}

          {/* Tier badge */}
          {tierStyle && (
            <Badge
              className={`absolute bottom-3 left-3 ${tierStyle.badge} flex items-center gap-1 shadow-md rounded-full px-2.5 py-1`}
              data-testid={`tier-badge-${tier}`}
            >
              <Crown className="h-3 w-3" />
              {tierStyle.label}
            </Badge>
          )}

          {/* Showcase / sample listing badge — soft so it doesn't compete with
              the price + photo, but visible enough to set expectations. */}
          {property.is_showcase && (
            <Badge
              className="absolute bottom-3 right-3 bg-white/95 text-[#5b35a8] border border-[#7042cc]/30 hover:bg-white shadow-sm rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wide uppercase"
              data-testid="showcase-badge"
            >
              Sample
            </Badge>
          )}
        </div>

        <CardContent className="p-5">
          {/* Price — brand purple */}
          <div className="mb-2">
            <span className="text-2xl font-bold text-[#5b35a8]">
              {fmtPrice(property.price)}
            </span>
            {property.type === 'rent' && (
              <span className="text-gray-500 text-sm ml-1">/month</span>
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 leading-snug">
            {property.title}
          </h3>

          {/* Location */}
          <div className="flex items-center text-gray-500 mb-3">
            <MapPin className="h-4 w-4 mr-1" />
            <span className="text-sm">
              {property.location.area}, {property.location.county}
            </span>
            {property.location?.lat != null && property.location?.lng != null && (
              <div className="ml-2">
                <NeighbourhoodScorecard
                  lat={property.location.lat}
                  lng={property.location.lng}
                  variant="badge"
                />
              </div>
            )}
          </div>

          {/* Spec row */}
          <div className="flex items-center gap-4 text-gray-600 text-sm">
            <div className="flex items-center">
              <Bed className="h-4 w-4 mr-1 text-[#7042cc]/70" />
              <span>{property.bedrooms} beds</span>
            </div>
            <div className="flex items-center">
              <Bath className="h-4 w-4 mr-1 text-[#7042cc]/70" />
              <span>{property.bathrooms} baths</span>
            </div>
            <div className="flex items-center">
              <Maximize className="h-4 w-4 mr-1 text-[#7042cc]/70" />
              <span>{property.size} m²</span>
            </div>
          </div>

          {/* Energy rating pill (BER for IE, EPC for UK) + Honesty Index */}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
            {(property.ber || property.epc) && (
              <Badge
                variant="outline"
                className="text-xs bg-[#F5EFFF] text-[#5b35a8] border-[#7042cc]/15 rounded-full"
                data-testid="property-card-energy-pill"
              >
                {country.energyLabel} {property.epc || property.ber}
              </Badge>
            )}
            {property.userId ? (
              <HonestyBadge userId={property.userId} compact />
            ) : null}
            {secureAgency && (
              <Badge
                variant="outline"
                className="text-[10px] bg-purple-50 text-purple-800 border-purple-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1 font-semibold"
                title="Verified secure account — email confirmed and consistent sign-in pattern"
                data-testid="property-card-secure-pill"
              >
                <ShieldCheck className="h-3 w-3" />
                Verified secure
              </Badge>
            )}
          </div>

          {/* Attribution pill for seeded listings — transparency guardrail
              and doubles as an entry-point to the claim flow (via detail
              page CTA). Only rendered when a source URL is on file. */}
          {property.is_external && property.source_url && (
            <div className="mt-2 flex items-center">
              <SourceAttribution
                sourceUrl={property.source_url}
                claimed={!!property.claimed_by_user_id}
                variant="card"
              />
            </div>
          )}
        </CardContent>
      </Link>
    </Card>
  );
};

export default PropertyCard;
export { resolveTier };
